'use client';

import { integrationSetupReturnPath } from '@/lib/integrations/setup-return';
import { useIntegrationSetupFlow } from './useIntegrationSetupFlow';
import { IntegrationSetupError } from './IntegrationSetupError';
import { useConnectorDraft } from './useConnectorDraft';
import { IntegrationResourcePicker } from './IntegrationResourcePicker';

import {
  Badge,
  Box,
  Button,
  Collapsible,
  Field,
  Grid,
  HStack,
  Input,
  Link,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  AlertCircle,
  Check,
  ChevronDown,
  Plus,
  ExternalLink,
  KeyRound,
  RefreshCw,
  Server,
  ShieldCheck,
  Unlink,
  Wrench,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CapabilityStatus } from '@/components/settings/CapabilityCatalog';
import { useAuth } from '@/app/auth-provider';
import { isAttuneDesktopShell } from '@/lib/desktop-auth-client';
import {
  IntegrationCard,
  IntegrationNotice,
  IntegrationStatusRow,
} from '@/components/shared/integrations/IntegrationCardShell';
import { InlineSpinner } from '@/components/shared/PageLoader';
import { AttuneSelect } from '@/components/shared/ui/AttuneSelect';
import { useProject } from '@/contexts/ProjectContext';

type ScopeType = 'project' | 'workspace';
type AuthMode = 'none' | 'bearer' | 'api_key_header' | 'oauth';
type StatusTone = 'connected' | 'warning' | 'idle' | 'danger';

interface CustomMcpTool {
  id: string;
  upstreamToolName: string;
  attuneToolName: string;
  description: string | null;
  enabled: boolean;
  lastSeenAt: string | null;
}

interface CustomMcpConnection {
  id: string;
  scopeType: ScopeType;
  displayName: string;
  endpointUrl: string;
  authMode: AuthMode;
  status: string;
  statusDetail: string | null;
  exposeToMcp: boolean;
  verifiedAt: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  toolCount: number;
  enabledToolCount: number;
  tools: CustomMcpTool[];
}

interface CustomMcpStatus {
  projectId: string;
  workspaceId: string | null;
  connections: CustomMcpConnection[];
}

interface CustomMcpPreset {
  id: string;
  name: string;
  endpointUrl: string;
  authMode: Extract<AuthMode, 'none' | 'oauth'>;
  description: string;
  requirement: string;
  docsUrl: string;
}

const EMPTY_STATUS: CustomMcpStatus = {
  projectId: '',
  workspaceId: null,
  connections: [],
};

const CUSTOM_MCP_PRESETS: CustomMcpPreset[] = [
  {
    id: 'semrush',
    name: 'Semrush',
    endpointUrl: 'https://mcp.semrush.com/v2/mcp',
    authMode: 'oauth',
    description: 'SEO, traffic, competitors, backlinks, and project reporting.',
    requirement: 'Eligible Semrush plan and API units required.',
    docsUrl: 'https://developer.semrush.com/api/v3/introduction/semrush-mcp/',
  },
  {
    id: 'hubspot',
    name: 'HubSpot CRM',
    endpointUrl: 'https://mcp.hubspot.com',
    authMode: 'oauth',
    description: 'CRM, campaign, content, and marketing activity context.',
    requirement: 'Requires a HubSpot MCP auth app with OAuth PKCE.',
    docsUrl:
      'https://developers.hubspot.com/docs/apps/developer-platform/build-apps/integrate-with-the-remote-hubspot-mcp-server',
  },
  {
    id: 'exa',
    name: 'Exa',
    endpointUrl: 'https://mcp.exa.ai/mcp',
    authMode: 'oauth',
    description: 'AI-native web search, page retrieval, company research, and enrichment.',
    requirement: 'Anonymous use is rate-limited; OAuth unlocks higher limits and Exa Agent.',
    docsUrl: 'https://exa.ai/docs/reference/exa-mcp',
  },
  {
    id: 'tavily',
    name: 'Tavily',
    endpointUrl: 'https://mcp.tavily.com/mcp',
    authMode: 'oauth',
    description: 'Current web search, extraction, crawling, site mapping, and research.',
    requirement: 'OAuth is supported; an API key can also be supplied manually.',
    docsUrl: 'https://docs.tavily.com/documentation/mcp',
  },
  {
    id: 'apify',
    name: 'Apify',
    endpointUrl: 'https://mcp.apify.com',
    authMode: 'oauth',
    description: 'Run vetted web data Actors for market, competitor, and content research.',
    requirement: 'OAuth is supported; available Actors and usage depend on the Apify account.',
    docsUrl: 'https://docs.apify.com/integrations/mcp',
  },
];

function formatDate(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function splitScopes(value: string): string[] {
  return value
    .split(/[,\s]+/)
    .map(scope => scope.trim())
    .filter(Boolean);
}

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const payload = await response.json();
    return typeof payload?.error === 'string' ? payload.error : fallback;
  } catch {
    return fallback;
  }
}

function hasConnectionError(connection: CustomMcpConnection): boolean {
  const status = connection.status.toLowerCase();
  const syncStatus = (connection.lastSyncStatus || '').toLowerCase();
  return (
    status.includes('error') ||
    status.includes('fail') ||
    syncStatus.includes('error') ||
    syncStatus.includes('fail') ||
    Boolean(connection.lastSyncError)
  );
}

function showCustomMcpReturnToast(error: string | null, success: string | null) {
  if (success === 'true' || success === '1') {
    toast.success('Custom MCP connected.');
    return;
  }
  if (!error) return;
  if (error === 'sync_failed') {
    toast.warning('Custom MCP connected, but tool sync failed.');
    return;
  }
  toast.error('Custom MCP authorization failed.');
}

function statusColor(tone: StatusTone): string {
  if (tone === 'connected') return 'status.success.fg';
  if (tone === 'warning') return 'status.warning.fg';
  if (tone === 'danger') return 'status.danger.fg';
  return 'ui.text.tertiary';
}

function connectionStatusColor(status: string | null | undefined): string {
  const normalized = (status || '').toLowerCase();
  if (['connected', 'ready', 'ok', 'active', 'success', 'synced'].includes(normalized)) {
    return 'status.success.fg';
  }
  if (['error', 'failed', 'revoked', 'disconnected'].includes(normalized))
    return 'status.danger.fg';
  if (['pending', 'syncing', 'testing'].includes(normalized)) return 'ui.text.tertiary';
  return 'status.warning.fg';
}

export function CustomMcpIntegration({ onStatusChanged }: { onStatusChanged?: () => void }) {
  const { user } = useAuth();
  const { currentProject, manageableWorkspaces } = useProject();
  const projectId = currentProject?.id || null;
  const setup = useIntegrationSetupFlow(projectId || undefined);
  const workspaceId = currentProject?.workspace_id || null;
  const canUseWorkspaceScope = Boolean(
    workspaceId && manageableWorkspaces.some(workspace => workspace.id === workspaceId)
  );

  const [setupOpen, setSetupOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [status, setStatus] = useState<CustomMcpStatus>(EMPTY_STATUS);
  const [connecting, setConnecting] = useState(false);
  const [syncingConnectionId, setSyncingConnectionId] = useState<string | null>(null);
  const [disconnectingConnectionId, setDisconnectingConnectionId] = useState<string | null>(null);
  const [updatingConnectionId, setUpdatingConnectionId] = useState<string | null>(null);
  const [updatingToolId, setUpdatingToolId] = useState<string | null>(null);

  const [displayName, setDisplayName] = useState('');
  const [endpointUrl, setEndpointUrl] = useState('');
  const [scopeType, setScopeType] = useState<ScopeType>('project');
  const [authMode, setAuthMode] = useState<AuthMode>('none');
  const [bearerToken, setBearerToken] = useState('');
  const [apiKeyHeaderName, setApiKeyHeaderName] = useState('x-api-key');
  const [apiKeyHeaderValue, setApiKeyHeaderValue] = useState('');
  const [authorizeUrl, setAuthorizeUrl] = useState('');
  const [tokenUrl, setTokenUrl] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [scopes, setScopes] = useState('');
  const [resource, setResource] = useState('');
  const [usePkce, setUsePkce] = useState(false);
  const [showAdvancedOAuth, setShowAdvancedOAuth] = useState(false);
  const [exposeToMcp, setExposeToMcp] = useState(true);

  const clearConnectionDraft = useConnectorDraft(
    user?.uid,
    projectId,
    'custom-mcp',
    { displayName, endpointUrl, authMode },
    draft => {
      setDisplayName(draft.displayName || '');
      setEndpointUrl(draft.endpointUrl || '');
      if (['none', 'bearer', 'api_key_header', 'oauth'].includes(draft.authMode))
        setAuthMode(draft.authMode as AuthMode);
    }
  );

  const applyPreset = useCallback((preset: CustomMcpPreset) => {
    setDisplayName(preset.name);
    setEndpointUrl(preset.endpointUrl);
    setAuthMode(preset.authMode);
    setBearerToken('');
    setApiKeyHeaderValue('');
    setAuthorizeUrl('');
    setTokenUrl('');
    setClientId('');
    setClientSecret('');
    setScopes('');
    setResource('');
    setUsePkce(preset.authMode === 'oauth');
    setShowAdvancedOAuth(false);
    setExposeToMcp(true);
    toast.success(`${preset.name} preset loaded. Review access requirements, then connect.`);
  }, []);

  useEffect(() => {
    if (!canUseWorkspaceScope && scopeType === 'workspace') {
      setScopeType('project');
    }
  }, [canUseWorkspaceScope, scopeType]);

  const fetchStatus = useCallback(
    async (options: { quiet?: boolean } = {}) => {
      if (!user || !projectId) {
        setStatus(EMPTY_STATUS);
        setLoading(false);
        return;
      }

      if (!options.quiet) setLoading(true);
      setLoadError(null);
      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `/api/integrations/custom-mcp/status?projectId=${encodeURIComponent(projectId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!response.ok) {
          throw new Error(await readErrorMessage(response, 'Failed to load Custom MCP status'));
        }
        const payload = (await response.json()) as CustomMcpStatus;
        setStatus({
          ...EMPTY_STATUS,
          ...payload,
          workspaceId: payload.workspaceId || workspaceId,
          connections: Array.isArray(payload.connections) ? payload.connections : [],
        });
        return payload;
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load Custom MCP status';
        setLoadError(message);
        console.error('Custom MCP status error:', error);
      } finally {
        setLoading(false);
      }
    },
    [projectId, user, workspaceId]
  );

  useEffect(() => {
    void fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('custom_mcp_success');
    const error = params.get('custom_mcp_error');
    const returnedConnectionId = params.get('connectionId');
    if (!success && !error) return;

    if (error)
      setup.reportError(
        error === 'sync_failed'
          ? 'Connected, but tools could not be loaded. Retry tool sync below.'
          : error === 'denied'
            ? 'Connection cancelled. Your server details are saved; connect when ready.'
            : 'Authorization failed. Review the server details and try again.'
      );
    else showCustomMcpReturnToast(null, success);

    params.delete('custom_mcp_success');
    params.delete('custom_mcp_error');
    params.delete('connectionId');
    const next = params.toString();
    window.history.replaceState(
      {},
      '',
      next ? `${window.location.pathname}?${next}` : window.location.pathname
    );
    void fetchStatus({ quiet: true }).then(payload => {
      onStatusChanged?.();
      const connection = payload?.connections.find(item => item.id === returnedConnectionId);
      if (
        success === 'true' &&
        !error &&
        connection?.status === 'connected' &&
        connection.tools.length > 0
      ) {
        clearConnectionDraft();
        setup.onConfigured();
      }
    });
  }, [fetchStatus, onStatusChanged, setup.onConfigured, clearConnectionDraft]);

  const connections = status.connections;
  const totalToolCount = connections.reduce(
    (sum, connection) => sum + Math.max(0, connection.toolCount || connection.tools.length),
    0
  );
  const enabledToolCount = connections.reduce(
    (sum, connection) => sum + Math.max(0, connection.enabledToolCount || 0),
    0
  );
  const exposedConnectionCount = connections.filter(connection => connection.exposeToMcp).length;
  const hasError = Boolean(loadError) || connections.some(hasConnectionError);

  const summary = useMemo((): { label: string; tone: StatusTone; detail: string } => {
    if (connections.length === 0) {
      return { label: 'Not connected', tone: 'idle', detail: 'No custom MCP connections.' };
    }
    if (hasError) {
      return {
        label: 'Needs attention',
        tone: 'danger',
        detail: `${connections.length} connections, ${enabledToolCount}/${totalToolCount} tools enabled.`,
      };
    }
    if (exposedConnectionCount > 0 && enabledToolCount > 0) {
      return {
        label: 'Live',
        tone: 'connected',
        detail: `${exposedConnectionCount} connections, ${enabledToolCount}/${totalToolCount} tools enabled.`,
      };
    }
    return {
      label: 'Connected',
      tone: 'warning',
      detail: `${connections.length} connections, no tools exposed yet.`,
    };
  }, [connections.length, enabledToolCount, exposedConnectionCount, hasError, totalToolCount]);

  const buildScopeBody = () => ({
    projectId,
    ...(scopeType === 'workspace' && workspaceId ? { workspaceId } : {}),
    scopeType,
  });

  const validateBaseForm = () => {
    if (!projectId) {
      setup.reportError('Select a project first.');
      return false;
    }
    if (!displayName.trim()) {
      setup.reportError('Enter a display name.');
      return false;
    }
    if (!endpointUrl.trim()) {
      setup.reportError('Enter an endpoint URL.');
      return false;
    }
    if (authMode === 'bearer' && !bearerToken.trim()) {
      setup.reportError('Enter a bearer token.');
      return false;
    }
    if (authMode === 'api_key_header' && (!apiKeyHeaderName.trim() || !apiKeyHeaderValue.trim())) {
      setup.reportError('Enter the API key header name and value.');
      return false;
    }
    if (
      authMode === 'oauth' &&
      showAdvancedOAuth &&
      (!authorizeUrl.trim() || !tokenUrl.trim() || !clientId.trim())
    ) {
      setup.reportError('Enter the OAuth URLs and client ID, or use auto-discover.');
      return false;
    }
    return true;
  };

  const clearSecrets = () => {
    setBearerToken('');
    setApiKeyHeaderValue('');
    setClientSecret('');
  };

  const handleConnect = async () => {
    if (!user || !validateBaseForm()) return;

    setup.clearError();
    setConnecting(true);
    try {
      const token = await user.getIdToken();
      const baseBody = {
        ...buildScopeBody(),
        displayName: displayName.trim(),
        endpointUrl: endpointUrl.trim(),
        exposeToMcp,
      };

      if (authMode === 'oauth') {
        const response = await fetch('/api/integrations/custom-mcp/oauth/start', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...baseBody,
            ...(showAdvancedOAuth
              ? {
                  authorizeUrl: authorizeUrl.trim(),
                  tokenUrl: tokenUrl.trim(),
                  clientId: clientId.trim(),
                  clientSecret,
                  scopes: splitScopes(scopes),
                  resource: resource.trim() || undefined,
                  usePkce,
                }
              : {}),
            returnTo: integrationSetupReturnPath('custom_mcp', setup.returnTo),
            desktopHandoff: isAttuneDesktopShell(),
          }),
        });
        const payload = (await response.json().catch(() => ({}))) as {
          authorizationUrl?: string;
          error?: string;
        };
        if (!response.ok || !payload.authorizationUrl) {
          throw new Error(payload.error || 'Failed to start OAuth');
        }
        clearSecrets();
        window.location.href = payload.authorizationUrl;
        return;
      }

      const response = await fetch('/api/integrations/custom-mcp/connect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...baseBody,
          authMode,
          bearerToken: authMode === 'bearer' ? bearerToken : undefined,
          apiKeyHeaderName: authMode === 'api_key_header' ? apiKeyHeaderName.trim() : undefined,
          apiKeyHeaderValue: authMode === 'api_key_header' ? apiKeyHeaderValue : undefined,
          syncTools: true,
        }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Connection failed'));
      }
      const payload = (await response.json().catch(() => ({}))) as { syncWarning?: string };
      if (payload.syncWarning) {
        toast.warning(`Custom MCP connected, but tool sync failed: ${payload.syncWarning}`);
      } else {
        toast.success('Custom MCP connected and synced.');
      }
      setDisplayName('');
      setEndpointUrl('');
      setAuthMode('none');
      clearSecrets();
      await fetchStatus({ quiet: true });
      onStatusChanged?.();
      if (!payload.syncWarning) {
        clearConnectionDraft();
        setup.onConfigured();
      }
    } catch (error) {
      if (authMode === 'oauth' && !showAdvancedOAuth) setShowAdvancedOAuth(true);
      setup.reportError(error instanceof Error ? error.message : 'Connection failed');
    } finally {
      setConnecting(false);
    }
  };

  const syncTools = async (connection: CustomMcpConnection) => {
    if (!user) return;
    setSyncingConnectionId(connection.id);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/custom-mcp/sync-tools', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ connectionId: connection.id }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Tool sync failed'));
      }
      toast.success('Tools synced.');
      await fetchStatus({ quiet: true });
      onStatusChanged?.();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Tool sync failed');
    } finally {
      setSyncingConnectionId(null);
    }
  };

  const updateConnectionExposure = async (connection: CustomMcpConnection, nextValue: boolean) => {
    if (!user) return;
    setUpdatingConnectionId(connection.id);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/custom-mcp/tools', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ connectionId: connection.id, exposeToMcp: nextValue }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'MCP exposure update failed'));
      }
      toast.success(nextValue ? 'Connection available to MCP.' : 'Connection hidden from MCP.');
      await fetchStatus({ quiet: true });
      onStatusChanged?.();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'MCP exposure update failed');
    } finally {
      setUpdatingConnectionId(null);
    }
  };

  const updateToolEnabled = async (
    connection: CustomMcpConnection,
    tool: CustomMcpTool,
    nextValue: boolean
  ) => {
    if (!user) return;
    setUpdatingToolId(tool.id);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/custom-mcp/tools', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ connectionId: connection.id, toolId: tool.id, enabled: nextValue }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Tool update failed'));
      }
      toast.success(nextValue ? 'Tool enabled.' : 'Tool disabled.');
      await fetchStatus({ quiet: true });
      onStatusChanged?.();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Tool update failed');
    } finally {
      setUpdatingToolId(null);
    }
  };

  const disconnectConnection = async (connection: CustomMcpConnection) => {
    if (!user) return;
    if (!window.confirm(`Disconnect ${connection.displayName}?`)) return;

    setDisconnectingConnectionId(connection.id);
    try {
      const token = await user.getIdToken();
      const response = await fetch('/api/integrations/custom-mcp/disconnect', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ connectionId: connection.id }),
      });
      if (!response.ok) {
        throw new Error(await readErrorMessage(response, 'Disconnect failed'));
      }
      toast.success('Custom MCP disconnected.');
      await fetchStatus({ quiet: true });
      onStatusChanged?.();
    } catch (error) {
      setup.reportError(error instanceof Error ? error.message : 'Disconnect failed');
    } finally {
      setDisconnectingConnectionId(null);
    }
  };

  if (loading) {
    return (
      <IntegrationCard
        icon={<Server size={18} />}
        title="Custom MCP"
        description="HTTP MCP servers proxied through Attune agent access."
        statusLabel="Loading"
        statusTone="idle"
      >
        <IntegrationSetupError message={setup.error} />
        <IntegrationStatusRow icon={<InlineSpinner size="xs" />} title="Loading status" />
      </IntegrationCard>
    );
  }

  return (
    <IntegrationCard
      icon={<Server size={18} />}
      title="Custom MCP"
      description="Connect external HTTP MCP servers and expose selected tools through Attune MCP."
      statusLabel={summary.label}
      statusTone={summary.tone}
      meta={<Text as="span">{summary.detail}</Text>}
    >
      <IntegrationSetupError message={setup.error} />
      {loadError && (
        <IntegrationNotice tone="danger" icon={<AlertCircle size={16} />}>
          {loadError}
        </IntegrationNotice>
      )}

      <VStack align="stretch" gap="4" py="3">
        <HStack justify="space-between" gap="3">
          <Box>
            <Text fontSize="sm" fontWeight="600" color="ui.text.primary">
              External servers
            </Text>
            <Text mt="1" fontSize="xs" color="ui.text.secondary">
              Manage the tools available to your Attune agents.
            </Text>
          </Box>
          <Button
            display={setup.embedded ? 'none' : undefined}
            size="sm"
            bg="ui.text.primary"
            color="ui.surface.canvas"
            borderRadius="8px"
            onClick={() => setSetupOpen(true)}
          >
            <Plus size={15} />
            Add server
          </Button>
        </HStack>
        <IntegrationResourcePicker
          open={setup.embedded || setupOpen}
          busy={connecting}
          onOpenChange={setSetupOpen}
          title="Add MCP server"
        >
          <VStack align="stretch" gap="4">
            <CustomMcpPresetPicker onSelect={applyPreset} />

            <CustomMcpConnectForm
              displayName={displayName}
              endpointUrl={endpointUrl}
              scopeType={scopeType}
              authMode={authMode}
              bearerToken={bearerToken}
              apiKeyHeaderName={apiKeyHeaderName}
              apiKeyHeaderValue={apiKeyHeaderValue}
              authorizeUrl={authorizeUrl}
              tokenUrl={tokenUrl}
              clientId={clientId}
              clientSecret={clientSecret}
              scopes={scopes}
              resource={resource}
              usePkce={usePkce}
              showAdvancedOAuth={showAdvancedOAuth}
              exposeToMcp={exposeToMcp}
              canUseWorkspaceScope={canUseWorkspaceScope}
              connecting={connecting}
              onDisplayNameChange={setDisplayName}
              onEndpointUrlChange={setEndpointUrl}
              onScopeTypeChange={setScopeType}
              onAuthModeChange={setAuthMode}
              onBearerTokenChange={setBearerToken}
              onApiKeyHeaderNameChange={setApiKeyHeaderName}
              onApiKeyHeaderValueChange={setApiKeyHeaderValue}
              onAuthorizeUrlChange={setAuthorizeUrl}
              onTokenUrlChange={setTokenUrl}
              onClientIdChange={setClientId}
              onClientSecretChange={setClientSecret}
              onScopesChange={setScopes}
              onResourceChange={setResource}
              onUsePkceChange={setUsePkce}
              onShowAdvancedOAuthChange={setShowAdvancedOAuth}
              onExposeToMcpChange={setExposeToMcp}
              onConnect={handleConnect}
            />
          </VStack>
        </IntegrationResourcePicker>

        <Box borderTopWidth="1px" borderTopStyle="solid" borderTopColor="ui.border.subtle" pt="3">
          <HStack justify="space-between" gap="3" mb="2" flexWrap="wrap">
            <HStack gap="2">
              <Wrench size={16} aria-hidden="true" />
              <Text fontWeight="600" color="ui.text.primary">
                Connections
              </Text>
            </HStack>
            <Badge bg="ui.surface.selected" color={statusColor(summary.tone)} variant="subtle">
              {enabledToolCount}/{totalToolCount} tools enabled
            </Badge>
          </HStack>

          {connections.length === 0 ? (
            <Text fontSize="sm" color="ui.text.secondary" py="3">
              No custom MCP servers connected.
            </Text>
          ) : (
            <VStack align="stretch" gap="0">
              {connections.map(connection => (
                <CustomMcpConnectionBlock
                  key={connection.id}
                  connection={connection}
                  syncing={syncingConnectionId === connection.id}
                  disconnecting={disconnectingConnectionId === connection.id}
                  updatingConnection={updatingConnectionId === connection.id}
                  updatingToolId={updatingToolId}
                  onSync={syncTools}
                  onDisconnect={disconnectConnection}
                  onExposeToMcpChange={updateConnectionExposure}
                  onToolEnabledChange={updateToolEnabled}
                />
              ))}
            </VStack>
          )}
        </Box>
      </VStack>
    </IntegrationCard>
  );
}

function CustomMcpPresetPicker({ onSelect }: { onSelect: (preset: CustomMcpPreset) => void }) {
  return (
    <Box
      borderBottomWidth="1px"
      borderBottomStyle="solid"
      borderBottomColor="ui.border.subtle"
      pb="4"
    >
      <HStack justify="space-between" align="start" gap="3" mb="3" flexWrap="wrap">
        <Box>
          <Text fontSize="sm" fontWeight="700" color="ui.text.primary">
            Quick presets
          </Text>
          <Text mt="0.5" fontSize="xs" color="ui.text.secondary">
            Verified vendor endpoints that prefill this connector. No integration is installed.
          </Text>
        </Box>
        <Badge bg="ui.surface.selected" color="ui.text.secondary" variant="subtle">
          Review before connecting
        </Badge>
      </HStack>

      <Grid
        templateColumns={{ base: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}
        columnGap={{ base: '0', md: '6' }}
      >
        {CUSTOM_MCP_PRESETS.map(preset => (
          <Box
            key={preset.id}
            textAlign="left"
            borderBottomWidth="1px"
            borderColor="ui.border.subtle"
            py="2.5"
          >
            <HStack justify="space-between" align="start" gap="3">
              <Box minW={0}>
                <HStack gap="2" flexWrap="wrap">
                  <Text fontSize="sm" fontWeight="700" color="ui.text.primary">
                    {preset.name}
                  </Text>
                  <Text fontSize="2xs" color="ui.text.tertiary" textTransform="uppercase">
                    {preset.authMode === 'oauth' ? 'OAuth' : 'No auth'}
                  </Text>
                </HStack>
                <Text mt="1" fontSize="xs" color="ui.text.secondary" lineHeight="1.5">
                  {preset.description}
                </Text>
                <Text mt="1" fontSize="2xs" color="ui.text.tertiary" lineHeight="1.45">
                  {preset.requirement}
                </Text>
              </Box>
              <VStack align="end" gap="1" flexShrink={0}>
                <Button size="xs" variant="ghost" onClick={() => onSelect(preset)}>
                  Use preset
                </Button>
                <Link
                  href={preset.docsUrl}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${preset.name} MCP documentation`}
                  color="ui.text.tertiary"
                  p="1"
                  _hover={{ color: 'ui.text.primary' }}
                >
                  <ExternalLink size={13} />
                </Link>
              </VStack>
            </HStack>
          </Box>
        ))}
      </Grid>
      <Text mt="2.5" fontSize="2xs" color="ui.text.tertiary" lineHeight="1.45">
        X account access is not preset: X currently publishes a documentation MCP, not an official
        hosted account-data MCP. A vetted X MCP URL can still be added manually.
      </Text>
    </Box>
  );
}

function CustomMcpConnectForm({
  displayName,
  endpointUrl,
  scopeType,
  authMode,
  bearerToken,
  apiKeyHeaderName,
  apiKeyHeaderValue,
  authorizeUrl,
  tokenUrl,
  clientId,
  clientSecret,
  scopes,
  resource,
  usePkce,
  showAdvancedOAuth,
  exposeToMcp,
  canUseWorkspaceScope,
  connecting,
  onDisplayNameChange,
  onEndpointUrlChange,
  onScopeTypeChange,
  onAuthModeChange,
  onBearerTokenChange,
  onApiKeyHeaderNameChange,
  onApiKeyHeaderValueChange,
  onAuthorizeUrlChange,
  onTokenUrlChange,
  onClientIdChange,
  onClientSecretChange,
  onScopesChange,
  onResourceChange,
  onUsePkceChange,
  onShowAdvancedOAuthChange,
  onExposeToMcpChange,
  onConnect,
}: {
  displayName: string;
  endpointUrl: string;
  scopeType: ScopeType;
  authMode: AuthMode;
  bearerToken: string;
  apiKeyHeaderName: string;
  apiKeyHeaderValue: string;
  authorizeUrl: string;
  tokenUrl: string;
  clientId: string;
  clientSecret: string;
  scopes: string;
  resource: string;
  usePkce: boolean;
  showAdvancedOAuth: boolean;
  exposeToMcp: boolean;
  canUseWorkspaceScope: boolean;
  connecting: boolean;
  onDisplayNameChange: (value: string) => void;
  onEndpointUrlChange: (value: string) => void;
  onScopeTypeChange: (value: ScopeType) => void;
  onAuthModeChange: (value: AuthMode) => void;
  onBearerTokenChange: (value: string) => void;
  onApiKeyHeaderNameChange: (value: string) => void;
  onApiKeyHeaderValueChange: (value: string) => void;
  onAuthorizeUrlChange: (value: string) => void;
  onTokenUrlChange: (value: string) => void;
  onClientIdChange: (value: string) => void;
  onClientSecretChange: (value: string) => void;
  onScopesChange: (value: string) => void;
  onResourceChange: (value: string) => void;
  onUsePkceChange: (value: boolean) => void;
  onShowAdvancedOAuthChange: (value: boolean) => void;
  onExposeToMcpChange: (value: boolean) => void;
  onConnect: () => void;
}) {
  return (
    <VStack align="stretch" gap="3">
      <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }} gap="3">
        <Field.Root>
          <Field.Label fontSize="xs">Display name</Field.Label>
          <Input
            size="sm"
            value={displayName}
            onChange={event => onDisplayNameChange(event.target.value)}
            placeholder="Internal MCP server"
          />
        </Field.Root>
        <Field.Root>
          <Field.Label fontSize="xs">Endpoint URL</Field.Label>
          <Input
            size="sm"
            value={endpointUrl}
            onChange={event => onEndpointUrlChange(event.target.value)}
            placeholder="https://example.com/mcp"
          />
        </Field.Root>
        <Field.Root>
          <Field.Label fontSize="xs">Scope</Field.Label>
          <AttuneSelect
            ariaLabel="Scope"
            options={[
              { value: 'project', label: 'Project' },
              ...(canUseWorkspaceScope ? [{ value: 'workspace', label: 'Workspace' }] : []),
            ]}
            value={scopeType}
            onValueChange={value => onScopeTypeChange(value as ScopeType)}
            size="sm"
          />
        </Field.Root>
        <Field.Root>
          <Field.Label fontSize="xs">Auth mode</Field.Label>
          <AttuneSelect
            ariaLabel="Auth mode"
            options={[
              { value: 'none', label: 'None' },
              { value: 'bearer', label: 'Bearer token' },
              { value: 'api_key_header', label: 'API key header' },
              { value: 'oauth', label: 'OAuth authorization code' },
            ]}
            value={authMode}
            onValueChange={value => onAuthModeChange(value as AuthMode)}
            size="sm"
          />
        </Field.Root>
      </Grid>

      {authMode === 'bearer' && (
        <Field.Root>
          <Field.Label fontSize="xs">Bearer token</Field.Label>
          <Input
            size="sm"
            type="password"
            value={bearerToken}
            onChange={event => onBearerTokenChange(event.target.value)}
            placeholder="Token"
          />
        </Field.Root>
      )}

      {authMode === 'api_key_header' && (
        <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }} gap="3">
          <Field.Root>
            <Field.Label fontSize="xs">Header name</Field.Label>
            <Input
              size="sm"
              value={apiKeyHeaderName}
              onChange={event => onApiKeyHeaderNameChange(event.target.value)}
              placeholder="x-api-key"
            />
          </Field.Root>
          <Field.Root>
            <Field.Label fontSize="xs">Header value</Field.Label>
            <Input
              size="sm"
              type="password"
              value={apiKeyHeaderValue}
              onChange={event => onApiKeyHeaderValueChange(event.target.value)}
              placeholder="API key"
            />
          </Field.Root>
        </Grid>
      )}

      {authMode === 'oauth' && (
        <HStack justify="space-between" gap="3" flexWrap="wrap">
          <Badge bg="accent.subtle" color="accent.fg" variant="subtle">
            Auto-discover OAuth
          </Badge>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => onShowAdvancedOAuthChange(!showAdvancedOAuth)}
          >
            {showAdvancedOAuth ? 'Use auto-discover' : 'Advanced OAuth'}
          </Button>
        </HStack>
      )}

      {authMode === 'oauth' && showAdvancedOAuth && (
        <Grid templateColumns={{ base: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }} gap="3">
          <Field.Root>
            <Field.Label fontSize="xs">Authorize URL</Field.Label>
            <Input
              size="sm"
              value={authorizeUrl}
              onChange={event => onAuthorizeUrlChange(event.target.value)}
              placeholder="https://example.com/oauth/authorize"
            />
          </Field.Root>
          <Field.Root>
            <Field.Label fontSize="xs">Token URL</Field.Label>
            <Input
              size="sm"
              value={tokenUrl}
              onChange={event => onTokenUrlChange(event.target.value)}
              placeholder="https://example.com/oauth/token"
            />
          </Field.Root>
          <Field.Root>
            <Field.Label fontSize="xs">Client ID</Field.Label>
            <Input
              size="sm"
              value={clientId}
              onChange={event => onClientIdChange(event.target.value)}
              placeholder="Client ID"
            />
          </Field.Root>
          <Field.Root>
            <Field.Label fontSize="xs">Client secret (optional)</Field.Label>
            <Input
              size="sm"
              type="password"
              value={clientSecret}
              onChange={event => onClientSecretChange(event.target.value)}
              placeholder="Client secret"
            />
          </Field.Root>
          <Field.Root gridColumn={{ base: 'auto', lg: '1 / -1' }}>
            <Field.Label fontSize="xs">Scopes</Field.Label>
            <Input
              size="sm"
              value={scopes}
              onChange={event => onScopesChange(event.target.value)}
              placeholder="read:tools offline_access"
            />
          </Field.Root>
          <Field.Root gridColumn={{ base: 'auto', lg: '1 / -1' }}>
            <Field.Label fontSize="xs">Resource (optional)</Field.Label>
            <Input
              size="sm"
              value={resource}
              onChange={event => onResourceChange(event.target.value)}
              placeholder="https://example.com/mcp"
            />
          </Field.Root>
          <HStack gap="2" gridColumn={{ base: 'auto', lg: '1 / -1' }}>
            <Switch.Root
              size="sm"
              checked={usePkce}
              onCheckedChange={event => onUsePkceChange(event.checked)}
            >
              <Switch.HiddenInput />
              <Switch.Control />
            </Switch.Root>
            <Text fontSize="sm" color="ui.text.secondary">
              Use PKCE
            </Text>
          </HStack>
        </Grid>
      )}

      <HStack justify="space-between" gap="3" flexWrap="wrap">
        <HStack gap="2">
          <Switch.Root
            size="sm"
            checked={exposeToMcp}
            onCheckedChange={event => onExposeToMcpChange(event.checked)}
          >
            <Switch.HiddenInput />
            <Switch.Control />
          </Switch.Root>
          <Text fontSize="sm" color="ui.text.secondary">
            Available to Attune MCP
          </Text>
        </HStack>

        <Button
          size="sm"
          bg="accent.solid"
          color="accent.contrast"
          borderRadius="glassButton"
          onClick={onConnect}
          disabled={connecting}
          _hover={{ bg: 'accent.emphasized' }}
        >
          {connecting ? (
            <InlineSpinner size="xs" />
          ) : authMode === 'oauth' ? (
            <ExternalLink size={14} />
          ) : (
            <Check size={14} />
          )}
          <Text ml="1">
            {connecting
              ? authMode === 'oauth'
                ? 'Starting...'
                : 'Testing...'
              : authMode === 'oauth'
                ? 'Start OAuth'
                : 'Connect / test & sync'}
          </Text>
        </Button>
      </HStack>
    </VStack>
  );
}

function CustomMcpConnectionBlock({
  connection,
  syncing,
  disconnecting,
  updatingConnection,
  updatingToolId,
  onSync,
  onDisconnect,
  onExposeToMcpChange,
  onToolEnabledChange,
}: {
  connection: CustomMcpConnection;
  syncing: boolean;
  disconnecting: boolean;
  updatingConnection: boolean;
  updatingToolId: string | null;
  onSync: (connection: CustomMcpConnection) => void;
  onDisconnect: (connection: CustomMcpConnection) => void;
  onExposeToMcpChange: (connection: CustomMcpConnection, nextValue: boolean) => void;
  onToolEnabledChange: (
    connection: CustomMcpConnection,
    tool: CustomMcpTool,
    nextValue: boolean
  ) => void;
}) {
  const errorMessage = connection.lastSyncError || connection.statusDetail;
  const hasError = hasConnectionError(connection);

  return (
    <Box borderTopWidth="1px" borderTopStyle="solid" borderTopColor="ui.border.subtle" py="2">
      <IntegrationStatusRow
        icon={hasError ? <AlertCircle size={16} /> : <Server size={16} />}
        title={connection.displayName}
        description={`${connection.scopeType} scope - ${connection.endpointUrl}`}
        action={
          <HStack gap="2" flexWrap="wrap" justify="flex-end">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onSync(connection)}
              disabled={syncing}
            >
              {syncing ? <InlineSpinner size="xs" /> : <RefreshCw size={14} />}
              <Text ml="1">{syncing ? 'Syncing...' : 'Sync tools'}</Text>
            </Button>
            <Button
              size="sm"
              variant="ghost"
              color="ui.text.tertiary"
              onClick={() => onDisconnect(connection)}
              disabled={disconnecting}
              _hover={{ color: 'status.danger.fg', bg: 'ui.surface.card' }}
            >
              {disconnecting ? <InlineSpinner size="xs" /> : <Unlink size={14} />}
              <Text ml="1">Disconnect</Text>
            </Button>
          </HStack>
        }
      >
        <HStack mt="2" gap="2" flexWrap="wrap">
          <CapabilityStatus
            label={connection.status}
            color={connectionStatusColor(connection.status)}
          />
          <Text fontSize="xs" color="ui.text.tertiary">
            {connection.authMode === 'api_key_header' ? 'API key header' : connection.authMode}
          </Text>

          <Text fontSize="xs" color={connectionStatusColor(connection.lastSyncStatus)}>
            Synced {formatDate(connection.lastSyncAt)}
          </Text>
          <HStack gap="2">
            <Switch.Root
              size="sm"
              checked={connection.exposeToMcp}
              aria-label={`Make ${connection.displayName} available to MCP`}
              disabled={updatingConnection}
              onCheckedChange={event => onExposeToMcpChange(connection, event.checked)}
            >
              <Switch.HiddenInput />
              <Switch.Control />
            </Switch.Root>
            <Text fontSize="xs" color="ui.text.tertiary">
              Available to MCP
            </Text>
          </HStack>
        </HStack>
      </IntegrationStatusRow>

      {hasError && errorMessage && (
        <IntegrationNotice icon={<AlertCircle size={16} />} tone="danger">
          {errorMessage}
        </IntegrationNotice>
      )}

      <Collapsible.Root>
        <Collapsible.Trigger asChild>
          <Button size="xs" variant="ghost" color="ui.text.secondary" mb="2" borderRadius="6px">
            <ChevronDown size={13} />
            {connection.tools.length} tools · Manage access
          </Button>
        </Collapsible.Trigger>
        <Collapsible.Content>
          {connection.tools.length === 0 ? (
            <Text fontSize="sm" color="ui.text.tertiary" py="2" pl={{ base: '0', md: '11' }}>
              No tools discovered yet.
            </Text>
          ) : (
            <VStack align="stretch" gap="0" pl={{ base: '0', md: '11' }}>
              {connection.tools.map(tool => (
                <CustomMcpToolRow
                  key={tool.id}
                  tool={tool}
                  updating={updatingToolId === tool.id}
                  onEnabledChange={(selectedTool, nextValue) =>
                    onToolEnabledChange(connection, selectedTool, nextValue)
                  }
                />
              ))}
            </VStack>
          )}
        </Collapsible.Content>
      </Collapsible.Root>
    </Box>
  );
}

function CustomMcpToolRow({
  tool,
  updating,
  onEnabledChange,
}: {
  tool: CustomMcpTool;
  updating: boolean;
  onEnabledChange: (tool: CustomMcpTool, nextValue: boolean) => void;
}) {
  return (
    <HStack
      justify="space-between"
      align="flex-start"
      gap="3"
      py="2"
      borderTopWidth="1px"
      borderTopStyle="solid"
      borderTopColor="ui.border.subtle"
    >
      <HStack align="flex-start" gap="2" minW={0}>
        <Box
          boxSize="7"
          borderRadius="glassButton"
          bg="ui.surface.selected"
          color="ui.text.secondary"
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
        >
          {tool.enabled ? <ShieldCheck size={14} /> : <KeyRound size={14} />}
        </Box>
        <Box minW={0}>
          <HStack gap="2" flexWrap="wrap">
            <Text fontSize="sm" fontWeight="600" color="ui.text.primary" wordBreak="break-word">
              {tool.attuneToolName}
            </Text>
            <Badge
              bg="ui.surface.selected"
              color={tool.enabled ? 'status.success.fg' : 'ui.text.tertiary'}
              variant="subtle"
              size="sm"
            >
              {tool.enabled ? 'Enabled' : 'Disabled'}
            </Badge>
          </HStack>
          <Text fontSize="xs" color="ui.text.tertiary" wordBreak="break-word">
            {tool.upstreamToolName}
            {tool.description ? ` - ${tool.description}` : ''}
          </Text>
        </Box>
      </HStack>
      <Switch.Root
        size="sm"
        checked={tool.enabled}
        aria-label={`Enable ${tool.attuneToolName}`}
        disabled={updating}
        onCheckedChange={event => onEnabledChange(tool, event.checked)}
      >
        <Switch.HiddenInput />
        <Switch.Control />
      </Switch.Root>
    </HStack>
  );
}
